import React, { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const INACTIVITY_REASONS = [
  "Sí, seguía trabajando fuera del CRM",
  "Estaba en llamada o reunión",
  "Estaba usando otra herramienta",
  "Pausa personal",
  "Comida",
  "Ausencia",
  "Otro",
];

/**
 * Avisos del módulo de Registro Horario (solo si el módulo está ACTIVO):
 *  - Al acceder sin haber iniciado jornada: propone iniciarla (login ≠ jornada).
 *  - Tras un periodo sin actividad en el CRM: pregunta si seguía trabajando (NO cierra jornada).
 * Si el módulo está apagado (go-live), este componente no hace nada.
 */
export default function JornadaGate() {
  const qc = useQueryClient();
  const { isImpersonating } = useAuth();
  // Silenciamos el gate cuando estamos impersonando: cualquier POST estaría bloqueado
  // por el middleware read-only, así que solo generaría ruido/errores. Además, la jornada
  // pertenece al usuario impersonado, no al admin — no queremos falsear su registro horario.
  const { data: cfg } = useQuery({
    queryKey: ["rh-config-gate"],
    queryFn: async () => (await api.get("/rh/config")).data,
    retry: false, staleTime: 5 * 60 * 1000,
    enabled: !isImpersonating,
  });
  const enabled = !!cfg?.enabled && !isImpersonating;
  const { data: today } = useQuery({
    queryKey: ["rh-today-gate"],
    queryFn: async () => (await api.get("/rh/jornada/today")).data,
    enabled, retry: false, refetchInterval: enabled ? 120000 : false,
  });

  const [showStart, setShowStart] = useState(false);
  const [showInactivity, setShowInactivity] = useState(false);
  const lastActivity = useRef(Date.now());
  const idleFlagged = useRef(false);
  const state = today?.state;

  // Prompt "iniciar jornada" (una vez por día, si no lo ha descartado)
  useEffect(() => {
    if (!enabled || !today) return;
    const dismissKey = `rh-start-dismissed-${today.date}`;
    if (state === "not_started" && !sessionStorage.getItem(dismissKey)) {
      setShowStart(true);
    }
  }, [enabled, today, state]);

  // Detección de inactividad (no cierra jornada; solo pregunta)
  useEffect(() => {
    if (!enabled) return;
    const threshold = (cfg?.inactivity_threshold_min || 30) * 60 * 1000;
    const mark = () => {
      lastActivity.current = Date.now();
      if (idleFlagged.current && state === "working") {
        idleFlagged.current = false;
        setShowInactivity(true);
      }
    };
    const evts = ["mousemove", "keydown", "click", "scroll"];
    evts.forEach((e) => window.addEventListener(e, mark, { passive: true }));
    const iv = setInterval(() => {
      if (state === "working" && Date.now() - lastActivity.current >= threshold) {
        idleFlagged.current = true;
      }
    }, 30000);
    return () => { evts.forEach((e) => window.removeEventListener(e, mark)); clearInterval(iv); };
  }, [enabled, cfg, state]);

  const startNow = async () => {
    try { await api.post("/rh/jornada/start"); toast.success("Jornada iniciada"); qc.invalidateQueries({ queryKey: ["rh-today-gate"] }); qc.invalidateQueries({ queryKey: ["rh-today"] }); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo iniciar"); }
    setShowStart(false);
  };
  const dismissStart = (remindLater) => {
    if (!remindLater && today) sessionStorage.setItem(`rh-start-dismissed-${today.date}`, "1");
    setShowStart(false);
  };
  const answerInactivity = async (reason) => {
    lastActivity.current = Date.now();
    try { await api.post("/rh/jornada/inactivity", { reason }); } catch { /* silencioso */ }
    setShowInactivity(false);
  };

  if (!enabled) return null;

  return (
    <>
      <Dialog open={showStart} onOpenChange={(o) => { if (!o) dismissStart(true); }}>
        <DialogContent className="sm:max-w-xl" data-testid="rh-start-prompt">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Clock className="h-5 w-5 text-primary" />Iniciar jornada</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Todavía no has iniciado tu jornada laboral. ¿Quieres iniciarla ahora?</p>
          <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-end sm:space-x-0">
            <Button variant="ghost" onClick={() => dismissStart(true)} data-testid="rh-start-later">Recordármelo más tarde</Button>
            <Button variant="outline" onClick={() => dismissStart(false)} data-testid="rh-start-no">Solo accedo puntualmente</Button>
            <Button onClick={startNow} data-testid="rh-start-yes"><Clock className="mr-1.5 h-4 w-4" />Iniciar jornada</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showInactivity} onOpenChange={(o) => { if (!o) setShowInactivity(false); }}>
        <DialogContent data-testid="rh-inactivity-prompt">
          <DialogHeader><DialogTitle>¿Seguías trabajando?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Hemos detectado un periodo sin actividad en el CRM. No cerramos tu jornada: solo queremos saber qué pasó.</p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {INACTIVITY_REASONS.map((r) => (
              <Button key={r} variant="outline" className="justify-start" onClick={() => answerInactivity(r)} data-testid={`rh-inactivity-${r.slice(0, 8)}`}>{r}</Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
